import { useEffect, useRef } from 'react';

/**
 * useVisiblePolling(callback, { intervalMs, enabled, deps })
 *
 * Calls `callback` every `intervalMs` while the tab is visible.
 * - Pauses when the tab is hidden (Page Visibility API) → zero background cost.
 * - Fires immediately when the tab regains focus.
 * - Automatically stops + restarts when `deps` change or the component unmounts.
 *
 * Use this for lightweight "background refresh" of data that is also mutated
 * externally (telegram replies, mobile writes, admin actions, etc.).
 *
 * Example:
 *   useVisiblePolling(async () => {
 *     const data = await api.getMessages();
 *     setMessages(data);
 *   }, { intervalMs: 15000, enabled: hasAccess });
 */
export function useVisiblePolling(callback, options = {}) {
  const { intervalMs = 20000, enabled = true, deps = [] } = options;
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const run = async () => {
      if (cancelled || document.hidden) return;
      try {
        await cbRef.current?.();
      } catch (err) {
        // Silent — next tick retries
        console.warn('useVisiblePolling callback failed:', err);
      }
    };

    const interval = setInterval(run, intervalMs);
    const onVisibility = () => {
      if (!document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, ...deps]);
}

export default useVisiblePolling;
